import React, { useState, useEffect, useContext } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Tabs, 
  Tab, 
  FormControlLabel, 
  Switch, 
  TextField, 
  Button,
  Grid,
  Divider,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import SaveIcon from '@material-ui/icons/Save';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { firestore } from '../../firebase/config';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { NotificationContext } from '../../contexts/NotificationContext';
import { SettingsContext } from '../../contexts/SettingsContext';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  tabPanel: {
    padding: theme.spacing(3),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  apiKeyField: {
    fontFamily: 'monospace',
  },
  colorSwatch: {
    width: 24,
    height: 24,
    marginRight: theme.spacing(1),
    border: '1px solid #ccc',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  historyItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
  },
  historyDate: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  historyUser: {
    fontWeight: 'bold',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

const SettingsPage = () => {
  const classes = useStyles();
  const { showNotification } = useContext(NotificationContext);
  const { settings, updateSettings } = useContext(SettingsContext);
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [regenerateKeyDialog, setRegenerateKeyDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [settingsHistory, setSettingsHistory] = useState([]);
  
  const [formData, setFormData] = useState({
    general: {
      appName: 'Voyage Admin',
      maintenanceMode: false,
      logoUrl: '',
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
      language: 'fr',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      currency: 'EUR',
      timezone: 'Europe/Paris'
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: false,
      smsNotifications: false,
      emailFrom: 'noreply@voyage-app.com',
      emailAdmin: 'admin@voyage-app.com',
      notifyOnNewBooking: true,
      notifyOnCancelledBooking: true,
      notifyOnNewUser: true,
      notifyOnPayment: true
    },
    security: {
      twoFactorAuth: true,
      sessionDuration: 60,
      passwordMinLength: 8,
      passwordRequireUppercase: true,
      passwordRequireNumber: true,
      passwordRequireSpecial: true,
      maxLoginAttempts: 5,
      ipRestriction: false,
      allowedIps: ''
    },
    api: {
      apiKey: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      apiEnabled: true,
      rateLimitPerMinute: 60,
      allowedOrigins: '*',
      logApiCalls: true
    }
  });
  
  const [errors, setErrors] = useState({});
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const settingsDoc = doc(firestore, 'settings', 'app_settings');
      const settingsSnapshot = await getDoc(settingsDoc);
      
      if (settingsSnapshot.exists()) {
        const settingsData = settingsSnapshot.data();
        setFormData(settingsData);
        
        // Mettre à jour le contexte global des paramètres
        if (updateSettings) {
          updateSettings(settingsData);
        }
      } else {
        // Si les paramètres n'existent pas, utiliser les valeurs par défaut
        // et les enregistrer dans Firestore
        await updateDoc(doc(firestore, 'settings', 'app_settings'), formData);
      }
      
      // Récupérer l'historique des modifications
      fetchSettingsHistory();
      
    } catch (error) {
      console.error('Error fetching settings:', error);
      showNotification('Erreur lors de la récupération des paramètres', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSettingsHistory = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const historyCollection = collection(firestore, 'settings_history');
      const historySnapshot = await getDocs(historyCollection);
      
      const historyData = [];
      historySnapshot.forEach(doc => {
        historyData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Trier par date décroissante
      historyData.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setSettingsHistory(historyData);
      
    } catch (error) {
      console.error('Error fetching settings history:', error);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleInputChange = (section, field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value
      }
    }));
    
    // Effacer l'erreur lorsque l'utilisateur modifie le champ
    if (errors[`${section}.${field}`]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[`${section}.${field}`];
        return newErrors;
      });
    }
  };
  
  const validateForm = (section) => {
    const newErrors = {};
    
    if (section === 'general' || section === 'all') {
      if (!formData.general.appName.trim()) {
        newErrors['general.appName'] = 'Le nom de l\'application est requis';
      }
    }
    
    if (section === 'notifications' || section === 'all') {
      if (formData.notifications.emailNotifications && !formData.notifications.emailFrom.trim()) {
        newErrors['notifications.emailFrom'] = 'L\'adresse d\'expédition est requise';
      }
      
      if (formData.notifications.emailNotifications && !formData.notifications.emailAdmin.trim()) {
        newErrors['notifications.emailAdmin'] = 'L\'adresse de l\'administrateur est requise';
      }
      
      // Valider le format des adresses email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.notifications.emailFrom && !emailRegex.test(formData.notifications.emailFrom)) {
        newErrors['notifications.emailFrom'] = 'Format d\'email invalide';
      }
      
      if (formData.notifications.emailAdmin && !emailRegex.test(formData.notifications.emailAdmin)) {
        newErrors['notifications.emailAdmin'] = 'Format d\'email invalide';
      }
    }
    
    if (section === 'security' || section === 'all') {
      if (formData.security.sessionDuration <= 0) {
        newErrors['security.sessionDuration'] = 'La durée doit être supérieure à 0';
      }
      
      if (formData.security.passwordMinLength < 6) {
        newErrors['security.passwordMinLength'] = 'La longueur minimale doit être d\'au moins 6 caractères';
      }
      
      if (formData.security.maxLoginAttempts <= 0) {
        newErrors['security.maxLoginAttempts'] = 'Le nombre de tentatives doit être supérieur à 0';
      }
      
      if (formData.security.ipRestriction && !formData.security.allowedIps.trim()) {
        newErrors['security.allowedIps'] = 'Veuillez spécifier au moins une adresse IP';
      }
    }
    
    if (section === 'api' || section === 'all') {
      if (formData.api.rateLimitPerMinute <= 0) {
        newErrors['api.rateLimitPerMinute'] = 'La limite doit être supérieure à 0';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSaveSettings = async (section) => {
    if (!validateForm(section)) {
      showNotification('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    
    setSaving(true);
    
    try {
      // Préparer les données pour l'enregistrement
      const dataToSave = { ...formData };
      
      // Simulation de mise à jour dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await updateDoc(doc(firestore, 'settings', 'app_settings'), dataToSave);
      
      // Enregistrer dans l'historique
      const historyEntry = {
        timestamp: new Date().toISOString(),
        userId: 'admin', // Dans une implémentation réelle, cela serait l'ID de l'utilisateur connecté
        userName: 'Administrateur', // Dans une implémentation réelle, cela serait le nom de l'utilisateur connecté
        section: section,
        changes: {}
      };
      
      // Mettre à jour le contexte global des paramètres
      if (updateSettings) {
        updateSettings(dataToSave);
      }
      
      showNotification('Paramètres enregistrés avec succès', 'success');
      
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Erreur lors de l\'enregistrement des paramètres', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRegenerateApiKey = async () => {
    try {
      // Générer une nouvelle clé API (UUID v4)
      const newApiKey = 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'.replace(/[x]/g, () => {
        return (Math.random() * 16 | 0).toString(16);
      });
      
      // Mettre à jour le formulaire
      handleInputChange('api', 'apiKey', newApiKey);
      
      // Fermer la boîte de dialogue
      setRegenerateKeyDialog(false);
      
      // Enregistrer les paramètres
      await handleSaveSettings('api');
      
      showNotification('Clé API régénérée avec succès', 'success');
      
    } catch (error) {
      console.error('Error regenerating API key:', error);
      showNotification('Erreur lors de la régénération de la clé API', 'error');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };
  
  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box className={classes.root}>
      <Typography variant="h4" component="h1" gutterBottom>
        Paramètres
      </Typography>
      
      <Paper className={classes.paper}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Général" />
          <Tab label="Notifications" />
          <Tab label="Sécurité" />
          <Tab label="API" />
          <Tab label="Historique" />
        </Tabs>
      </Paper>
      
      {/* Onglet Général */}
      {activeTab === 0 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Paramètres généraux
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom de l'application"
                variant="outlined"
                value={formData.general.appName}
                onChange={(e) => handleInputChange('general', 'appName', e.target.value)}
                error={!!errors['general.appName']}
                helperText={errors['general.appName']}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="URL du logo"
                variant="outlined"
                value={formData.general.logoUrl}
                onChange={(e) => handleInputChange('general', 'logoUrl', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="language-label">Langue par défaut</InputLabel>
                <Select
                  labelId="language-label"
                  value={formData.general.language}
                  onChange={(e) => handleInputChange('general', 'language', e.target.value)}
                  label="Langue par défaut"
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">Anglais</MenuItem>
                  <MenuItem value="es">Espagnol</MenuItem>
                  <MenuItem value="de">Allemand</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="currency-label">Devise par défaut</InputLabel>
                <Select
                  labelId="currency-label"
                  value={formData.general.currency}
                  onChange={(e) => handleInputChange('general', 'currency', e.target.value)}
                  label="Devise par défaut"
                >
                  <MenuItem value="EUR">Euro (€)</MenuItem>
                  <MenuItem value="USD">Dollar américain ($)</MenuItem>
                  <MenuItem value="GBP">Livre sterling (£)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="date-format-label">Format de date</InputLabel>
                <Select
                  labelId="date-format-label"
                  value={formData.general.dateFormat}
                  onChange={(e) => handleInputChange('general', 'dateFormat', e.target.value)}
                  label="Format de date"
                >
                  <MenuItem value="DD/MM/YYYY">JJ/MM/AAAA</MenuItem>
                  <MenuItem value="MM/DD/YYYY">MM/JJ/AAAA</MenuItem>
                  <MenuItem value="YYYY-MM-DD">AAAA-MM-JJ</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="time-format-label">Format d'heure</InputLabel>
                <Select
                  labelId="time-format-label"
                  value={formData.general.timeFormat}
                  onChange={(e) => handleInputChange('general', 'timeFormat', e.target.value)}
                  label="Format d'heure"
                >
                  <MenuItem value="24h">24h</MenuItem>
                  <MenuItem value="12h">12h (AM/PM)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="timezone-label">Fuseau horaire</InputLabel>
                <Select
                  labelId="timezone-label"
                  value={formData.general.timezone}
                  onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
                  label="Fuseau horaire"
                >
                  <MenuItem value="Europe/Paris">Europe/Paris</MenuItem>
                  <MenuItem value="Europe/London">Europe/Londres</MenuItem>
                  <MenuItem value="America/New_York">Amérique/New York</MenuItem>
                  <MenuItem value="Asia/Tokyo">Asie/Tokyo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.general.maintenanceMode}
                    onChange={(e) => handleInputChange('general', 'maintenanceMode', e.target.checked)}
                    color="primary"
                  />
                }
                label="Mode maintenance"
              />
            </Grid>
          </Grid>
          
          <Divider className={classes.divider} />
          
          <Typography variant="h6" className={classes.sectionTitle}>
            Personnalisation
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Couleur primaire"
                variant="outlined"
                value={formData.general.primaryColor}
                onChange={(e) => handleInputChange('general', 'primaryColor', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div 
                        className={classes.colorSwatch} 
                        style={{ backgroundColor: formData.general.primaryColor }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Couleur secondaire"
                variant="outlined"
                value={formData.general.secondaryColor}
                onChange={(e) => handleInputChange('general', 'secondaryColor', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div 
                        className={classes.colorSwatch} 
                        style={{ backgroundColor: formData.general.secondaryColor }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
              onClick={() => handleSaveSettings('general')}
              disabled={saving}
              className={classes.button}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Onglet Notifications */}
      {activeTab === 1 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Paramètres de notifications
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.emailNotifications}
                    onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Notifications par email"
              />
            </Grid>
            
            {formData.notifications.emailNotifications && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Adresse d'expédition"
                    variant="outlined"
                    value={formData.notifications.emailFrom}
                    onChange={(e) => handleInputChange('notifications', 'emailFrom', e.target.value)}
                    error={!!errors['notifications.emailFrom']}
                    helperText={errors['notifications.emailFrom']}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Adresse de l'administrateur"
                    variant="outlined"
                    value={formData.notifications.emailAdmin}
                    onChange={(e) => handleInputChange('notifications', 'emailAdmin', e.target.value)}
                    error={!!errors['notifications.emailAdmin']}
                    helperText={errors['notifications.emailAdmin']}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.pushNotifications}
                    onChange={(e) => handleInputChange('notifications', 'pushNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Notifications push"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.smsNotifications}
                    onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
                    color="primary"
                  />
                }
                label="Notifications SMS"
              />
            </Grid>
          </Grid>
          
          <Divider className={classes.divider} />
          
          <Typography variant="h6" className={classes.sectionTitle}>
            Événements de notification
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.notifyOnNewBooking}
                    onChange={(e) => handleInputChange('notifications', 'notifyOnNewBooking', e.target.checked)}
                    color="primary"
                  />
                }
                label="Nouvelle réservation"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.notifyOnCancelledBooking}
                    onChange={(e) => handleInputChange('notifications', 'notifyOnCancelledBooking', e.target.checked)}
                    color="primary"
                  />
                }
                label="Réservation annulée"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.notifyOnNewUser}
                    onChange={(e) => handleInputChange('notifications', 'notifyOnNewUser', e.target.checked)}
                    color="primary"
                  />
                }
                label="Nouvel utilisateur"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.notifications.notifyOnPayment}
                    onChange={(e) => handleInputChange('notifications', 'notifyOnPayment', e.target.checked)}
                    color="primary"
                  />
                }
                label="Nouveau paiement"
              />
            </Grid>
          </Grid>
          
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
              onClick={() => handleSaveSettings('notifications')}
              disabled={saving}
              className={classes.button}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Onglet Sécurité */}
      {activeTab === 2 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Paramètres de sécurité
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.security.twoFactorAuth}
                    onChange={(e) => handleInputChange('security', 'twoFactorAuth', e.target.checked)}
                    color="primary"
                  />
                }
                label="Authentification à deux facteurs"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Durée de session (minutes)"
                variant="outlined"
                type="number"
                value={formData.security.sessionDuration}
                onChange={(e) => handleInputChange('security', 'sessionDuration', parseInt(e.target.value))}
                error={!!errors['security.sessionDuration']}
                helperText={errors['security.sessionDuration']}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre maximal de tentatives de connexion"
                variant="outlined"
                type="number"
                value={formData.security.maxLoginAttempts}
                onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                error={!!errors['security.maxLoginAttempts']}
                helperText={errors['security.maxLoginAttempts']}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
          </Grid>
          
          <Divider className={classes.divider} />
          
          <Typography variant="h6" className={classes.sectionTitle}>
            Politique de mot de passe
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Longueur minimale du mot de passe"
                variant="outlined"
                type="number"
                value={formData.security.passwordMinLength}
                onChange={(e) => handleInputChange('security', 'passwordMinLength', parseInt(e.target.value))}
                error={!!errors['security.passwordMinLength']}
                helperText={errors['security.passwordMinLength']}
                InputProps={{
                  inputProps: { min: 6 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.security.passwordRequireUppercase}
                    onChange={(e) => handleInputChange('security', 'passwordRequireUppercase', e.target.checked)}
                    color="primary"
                  />
                }
                label="Exiger au moins une majuscule"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.security.passwordRequireNumber}
                    onChange={(e) => handleInputChange('security', 'passwordRequireNumber', e.target.checked)}
                    color="primary"
                  />
                }
                label="Exiger au moins un chiffre"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.security.passwordRequireSpecial}
                    onChange={(e) => handleInputChange('security', 'passwordRequireSpecial', e.target.checked)}
                    color="primary"
                  />
                }
                label="Exiger au moins un caractère spécial"
              />
            </Grid>
          </Grid>
          
          <Divider className={classes.divider} />
          
          <Typography variant="h6" className={classes.sectionTitle}>
            Restriction d'accès
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.security.ipRestriction}
                    onChange={(e) => handleInputChange('security', 'ipRestriction', e.target.checked)}
                    color="primary"
                  />
                }
                label="Restreindre l'accès par adresse IP"
              />
            </Grid>
            
            {formData.security.ipRestriction && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresses IP autorisées (séparées par des virgules)"
                  variant="outlined"
                  value={formData.security.allowedIps}
                  onChange={(e) => handleInputChange('security', 'allowedIps', e.target.value)}
                  error={!!errors['security.allowedIps']}
                  helperText={errors['security.allowedIps'] || "Ex: 192.168.1.1, 10.0.0.1"}
                  multiline
                  rows={2}
                />
              </Grid>
            )}
          </Grid>
          
          <Box className={classes.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
              onClick={() => handleSaveSettings('security')}
              disabled={saving}
              className={classes.button}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Onglet API */}
      {activeTab === 3 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Paramètres API
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.api.apiEnabled}
                    onChange={(e) => handleInputChange('api', 'apiEnabled', e.target.checked)}
                    color="primary"
                  />
                }
                label="Activer l'API"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Clé API"
                variant="outlined"
                value={showApiKey ? formData.api.apiKey : '••••••••-••••-••••-••••-••••••••••••'}
                InputProps={{
                  readOnly: true,
                  classes: {
                    input: classes.apiKeyField
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle api key visibility"
                        onClick={() => setShowApiKey(!showApiKey)}
                        edge="end"
                      >
                        {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Limite de requêtes par minute"
                variant="outlined"
                type="number"
                value={formData.api.rateLimitPerMinute}
                onChange={(e) => handleInputChange('api', 'rateLimitPerMinute', parseInt(e.target.value))}
                error={!!errors['api.rateLimitPerMinute']}
                helperText={errors['api.rateLimitPerMinute']}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Origines autorisées (CORS)"
                variant="outlined"
                value={formData.api.allowedOrigins}
                onChange={(e) => handleInputChange('api', 'allowedOrigins', e.target.value)}
                helperText="Utilisez * pour autoriser toutes les origines, ou spécifiez des domaines séparés par des virgules"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.api.logApiCalls}
                    onChange={(e) => handleInputChange('api', 'logApiCalls', e.target.checked)}
                    color="primary"
                  />
                }
                label="Journaliser les appels API"
              />
            </Grid>
          </Grid>
          
          <Box className={classes.buttonGroup}>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={() => setRegenerateKeyDialog(true)}
              className={classes.button}
            >
              Régénérer la clé API
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={24} /> : <SaveIcon />}
              onClick={() => handleSaveSettings('api')}
              disabled={saving}
              className={classes.button}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </Paper>
      )}
      
      {/* Onglet Historique */}
      {activeTab === 4 && (
        <Paper className={classes.paper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Historique des modifications
          </Typography>
          
          {settingsHistory.length === 0 ? (
            <Typography variant="body1">
              Aucune modification enregistrée.
            </Typography>
          ) : (
            settingsHistory.map((entry) => (
              <Paper key={entry.id} className={classes.historyItem} variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="body2" className={classes.historyDate}>
                      {formatDate(entry.timestamp)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      <span className={classes.historyUser}>{entry.userName}</span> a modifié les paramètres de <strong>{entry.section}</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            ))
          )}
        </Paper>
      )}
      
      {/* Boîte de dialogue de confirmation pour régénérer la clé API */}
      <Dialog
        open={regenerateKeyDialog}
        onClose={() => setRegenerateKeyDialog(false)}
        aria-labelledby="regenerate-key-dialog-title"
        aria-describedby="regenerate-key-dialog-description"
      >
        <DialogTitle id="regenerate-key-dialog-title">Confirmer la régénération</DialogTitle>
        <DialogContent>
          <DialogContentText id="regenerate-key-dialog-description">
            Êtes-vous sûr de vouloir régénérer la clé API ? Cette action invalidera immédiatement l'ancienne clé et toutes les applications qui l'utilisent devront être mises à jour.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenerateKeyDialog(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleRegenerateApiKey} color="secondary" autoFocus>
            Régénérer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingsPage;
